import Environment from './Environment';

const { API_URL } = Environment;

const Api = {
  chatWithAgent: async (message, conversationHistory, voiceEnabled = false) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ message, history: conversationHistory, voiceEnabled }),
    });

    if (!response.ok) {
        let errorData;
        try {
            errorData = await response.json();
        } catch (e) {
            errorData = { error: `HTTP error! status: ${response.status}` };
        }
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }

    return response.json();
  },

  login: async (email, password, token = null) => {
    const body = token ? { token } : { email, password };
    const response = await fetch(`${API_URL}/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });

    return response.json();
  },

  getProfile: async () => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/me`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
       let errorData;
       try {
         errorData = await response.json();
       } catch (e) {
         errorData = { error: `HTTP error! status: ${response.status}` };
       }
       const error = new Error(errorData.error || `HTTP error! status: ${response.status}`);
       error.response = { data: errorData, status: response.status };
       throw error;
    }

    return response.json();
  },

  register: async (name, email, password) => {
    const response = await fetch(`${API_URL}/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ name, email, password }),
    });

    return response.json();
  },

  post: async (endpoint, data, options = {}) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}${endpoint}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...options.headers,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
       let errorData;
       try {
         errorData = await response.json();
       } catch (e) {
         errorData = { error: `HTTP error! status: ${response.status}` };
       }
       const error = new Error(errorData.error || `HTTP error! status: ${response.status}`);
       error.response = { data: errorData, status: response.status };
       throw error;
    }

    return response.json();
  },

  get: async (endpoint, options = {}) => {
    const token = localStorage.getItem('authToken');
    let url = `${API_URL}${endpoint}`;

    if (options.params) {
      const queryString = new URLSearchParams(options.params).toString();
      url += `?${queryString}`;
    }

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...options.headers,
      },
    });

    if (!response.ok) {
       let errorData;
       try {
         errorData = await response.json();
       } catch (e) {
         errorData = { error: `HTTP error! status: ${response.status}` };
       }
       const error = new Error(errorData.error || `HTTP error! status: ${response.status}`);
       error.response = { data: errorData, status: response.status };
       throw error;
    }

    const data = await response.json();
    return { data };
  },

  post: async (endpoint, body, options = {}) => {
    const token = localStorage.getItem('authToken');
    
    const headers = {
      Authorization: `Bearer ${token}`,
      ...options.headers,
    };

    let requestBody = body;
    if (!(body instanceof FormData)) {
      if (!headers['Content-Type']) {
        headers['Content-Type'] = 'application/json';
      }
      requestBody = JSON.stringify(body);
    }

    const response = await fetch(`${API_URL}${endpoint}`, {
      method: 'POST',
      headers: headers,
      body: requestBody,
    });

    if (!response.ok) {
       let errorData;
       try {
         errorData = await response.json();
       } catch (e) {
         errorData = { error: `HTTP error! status: ${response.status}` };
       }
       const error = new Error(errorData.error || `HTTP error! status: ${response.status}`);
       error.response = { data: errorData, status: response.status };
       throw error;
    }

    const data = await response.json();
    return { data };
  },

  put: async (endpoint, body, options = {}) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}${endpoint}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...options.headers,
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
       let errorData;
       try {
         errorData = await response.json();
       } catch (e) {
         errorData = { error: `HTTP error! status: ${response.status}` };
       }
       const error = new Error(errorData.error || `HTTP error! status: ${response.status}`);
       error.response = { data: errorData, status: response.status };
       throw error;
    }

    const data = await response.json();
    return { data };
  },

  delete: async (endpoint, options = {}) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}${endpoint}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...options.headers,
      },
    });

    if (!response.ok) {
       let errorData;
       try {
         errorData = await response.json();
       } catch (e) {
         errorData = { error: `HTTP error! status: ${response.status}` };
       }
       const error = new Error(errorData.error || `HTTP error! status: ${response.status}`);
       error.response = { data: errorData, status: response.status };
       throw error;
    }

    try {
        const data = await response.json();
        return { data };
    } catch (e) {
        return { data: null };
    }
  },

  checkCrossPlatformLink: async () => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/cross-platform/link`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to check link');
    }

    return response.json();
  },

  getPublicInspection: async (token) => {
    const response = await fetch(`${API_URL}/inspection-executions/public/${token}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Falha ao carregar inspeção pública');
    }

    return response.json();
  },

  shareInspection: async (id) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/inspection-executions/${id}/share`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Falha ao gerar link de compartilhamento');
    }

    return response.json();
  },

  getCompanies: async () => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    return response.json();
  },

  getCompany: async (id) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${id}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    return response.json();
  },

  createCompany: async (data) => {
    const token = localStorage.getItem('authToken');
    const isFormData = data instanceof FormData;

    const headers = {
      Authorization: `Bearer ${token}`,
    };

    if (!isFormData) {
      headers['Content-Type'] = 'application/json';
    }

    const response = await fetch(`${API_URL}/companies`, {
      method: 'POST',
      headers,
      body: isFormData ? data : JSON.stringify(data),
    });

    return response.json();
  },

  updateCompany: async (id, data) => {
    const token = localStorage.getItem('authToken');
    const isFormData = data instanceof FormData;

    const headers = {
      Authorization: `Bearer ${token}`,
    };

    if (!isFormData) {
      headers['Content-Type'] = 'application/json';
    }

    const response = await fetch(`${API_URL}/companies/${id}`, {
      method: 'PUT',
      headers,
      body: isFormData ? data : JSON.stringify(data),
    });

    return response.json();
  },

  deleteCompany: async (id) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${id}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    return response;
  },

  listInspectionConfigs: async (params = {}) => {
    const token = localStorage.getItem('authToken');
    const queryString = new URLSearchParams(params).toString();
    const response = await fetch(`${API_URL}/inspection-configs?${queryString}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    return response.json();
  },

  getInspectionOverview: async (params = {}) => {
    const token = localStorage.getItem('authToken');
    const queryString = new URLSearchParams(params).toString();
    const response = await fetch(`${API_URL}/inspection-executions/overview?${queryString}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    return response.json();
  },

  getInspectionConfigs: async (companyId) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/inspection-configs`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    return response.json();
  },

  getInspectionConfigUsers: async (companyId) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/inspection-configs/users`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    return response.json();
  },

  getInspectionConfigById: async (companyId, id) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/inspection-configs/${id}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    return response.json();
  },

  createInspectionConfig: async (companyId, data) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/inspection-configs`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });
    return response.json();
  },

  updateInspectionConfig: async (companyId, id, data) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/inspection-configs/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });
    return response.json();
  },

  deleteInspectionConfig: async (companyId, id) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/inspection-configs/${id}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    return response;
  },

  getExecutedInspections: async (companyId, params = {}) => {
    const token = localStorage.getItem('authToken');
    const queryString = new URLSearchParams(params).toString();
    const response = await fetch(`${API_URL}/companies/${companyId}/inspection-executions?${queryString}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    return response.json();
  },

  getExecutedInspectionById: async (companyId, id) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/inspection-executions/${id}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    return response.json();
  },

  createExecutedInspection: async (companyId, data) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/inspection-executions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });
    return response.json();
  },

  updateExecutedInspection: async (companyId, id, data) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/inspection-executions/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });
    return response.json();
  },

  getExecutedInspection: async (companyId, id) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/inspection-executions/${id}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    return response.json();
  },

  deleteExecutedInspection: async (companyId, id) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/inspection-executions/${id}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    return response;
  },

  getFolders: async (companyId, parentId = null, serviceStationId = null, employeeId = null, category = 'geral', options = {}) => {
    const token = localStorage.getItem('authToken');
    let url = `${API_URL}/folders?company_id=${companyId}`;
    if (parentId) {
      url += `&parent_id=${parentId}`;
    }
    if (serviceStationId) {
      url += `&service_station_id=${serviceStationId}`;
    }
    if (employeeId) {
      url += `&employee_id=${employeeId}`;
    }
    if (category) {
      url += `&category=${category}`;
    }
    
    // New entity filters
    if (options.vehicleId) url += `&vehicle_id=${options.vehicleId}`;
    if (options.customerId) url += `&customer_id=${options.customerId}`;
    if (options.supplierId) url += `&supplier_id=${options.supplierId}`;
    if (options.accountantId) url += `&accountant_id=${options.accountantId}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    return response.json();
  },

  getFolder: async (id) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/folders/${id}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    return response.json();
  },

  createFolder: async (data) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/folders`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });
    return response.json();
  },

  updateFolder: async (id, data) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/folders/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });
    return response.json();
  },

  deleteFolder: async (id) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/folders/${id}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    return response;
  },

  createFile: async (data) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/files`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });
    return response.json();
  },

  updateFile: async (id, data) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/files/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });
    return response.json();
  },

  deleteFile: async (id) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/files/${id}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    return response;
  },

  getFiles: async (folderId, companyId = null, serviceStationId = null, employeeId = null, category = 'geral', options = {}) => {
    const token = localStorage.getItem('authToken');
    let url = `${API_URL}/files`;
    
    const params = new URLSearchParams();
    if (folderId) params.append('folder_id', folderId);
    if (companyId) params.append('company_id', companyId);
    if (serviceStationId) params.append('service_station_id', serviceStationId);
    if (employeeId) params.append('employee_id', employeeId);
    if (category) params.append('category', category);

    // New entity filters
    if (options.vehicleId) params.append('vehicle_id', options.vehicleId);
    if (options.customerId) params.append('customer_id', options.customerId);
    if (options.supplierId) params.append('supplier_id', options.supplierId);
    if (options.accountantId) params.append('accountant_id', options.accountantId);

    if (params.toString()) {
        url += `?${params.toString()}`;
    }
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    return response.json();
  },

  getFile: async (id) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/files/${id}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    return response.json();
  },

  getVehicles: async (companyId) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/vehicles`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    return response.json();
  },

  getMachineries: async (companyId) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/machineries`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    return response.json();
  },

  getServiceStations: async (companyId) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/stations`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    return response.json();
  },

  createServiceStation: async (companyId, data) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/stations`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });

    return response.json();
  },

  importServiceStations: async (companyId, data) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/stations/import`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });

    return response.json();
  },

  updateServiceStation: async (companyId, id, data) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/stations/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });

    return response.json();
  },

  deleteServiceStation: async (companyId, id) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/stations/${id}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    return response.json();
  },

  getServiceStationEmployees: async (companyId, stationId) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/stations/${stationId}/employees`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    return response.json();
  },

  addServiceStationEmployee: async (companyId, stationId, employeeId) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/stations/${stationId}/employees`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ employeeId }),
    });

    return response.json();
  },

  removeServiceStationEmployee: async (companyId, stationId, employeeId) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/stations/${stationId}/employees/${employeeId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    return response.json();
  },

  getPositions: async (companyId) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/positions`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    return response.json();
  },

  createPosition: async (companyId, data) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/positions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });

    return response.json();
  },

  updatePosition: async (companyId, id, data) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/positions/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });

    return response.json();
  },

  deletePosition: async (companyId, id) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/positions/${id}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    return response.json();
  },

  importPositions: async (companyId, data) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/positions/import`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });

    return response.json();
  },

  getBenefits: async (companyId) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/benefits`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    return response.json();
  },

  createBenefit: async (companyId, data) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/benefits`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });

    return response.json();
  },

  updateBenefit: async (companyId, id, data) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/benefits/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });

    return response.json();
  },

  deleteBenefit: async (companyId, id) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/benefits/${id}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    return response.json();
  },

  getJourneys: async (companyId) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/journeys`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    return response.json();
  },

  createJourney: async (companyId, data) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/journeys`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });
    return response.json();
  },

  importJourneys: async (companyId, data) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/journeys/import`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });
    return response.json();
  },

  importCustomers: async (companyId, data) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/customers/import`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });
    return response.json();
  },

  importSuppliers: async (companyId, data) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/suppliers/import`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });
    return response.json();
  },

  updateJourney: async (companyId, id, data) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/journeys/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });

    return response.json();
  },

  deleteJourney: async (companyId, id) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/journeys/${id}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    return response.json();
  },

  getEmployees: async (companyId) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/employees`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    return response.json();
  },

  createEmployee: async (companyId, data) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/employees`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });

    return response.json();
  },

  bulkCreateEmployees: async (companyId, employees) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/employees/bulk`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ employees }),
    });

    return response.json();
  },

  updateEmployee: async (companyId, id, data) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/employees/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });

    return response.json();
  },

  deleteEmployee: async (companyId, id) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/employees/${id}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    return response.json();
  },

  activateEmployee: async (companyId, id) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/employees/${id}/activate`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    return response.json();
  },

  transferEmployee: async (companyId, id, data) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/employees/${id}/transfer`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });

    return response.json();
  },

  // Customers
  getCustomers: async (companyId) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/customers`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    return response.json();
  },

  createCustomer: async (companyId, data) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/customers`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });
    return response.json();
  },

  updateCustomer: async (companyId, id, data) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/customers/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });
    return response.json();
  },

  deleteCustomer: async (companyId, id) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/customers/${id}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    if (response.status === 204) return true;
    return response.json();
  },

  // SubUsers
  getSubUsers: async () => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/sub-users`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    return response.json();
  },

  createSubUser: async (data) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/sub-users`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });
    return response.json();
  },

  updateSubUser: async (id, data) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/sub-users/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });
    return response.json();
  },

  deleteSubUser: async (id) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/sub-users/${id}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    if (response.status === 204) return true;
    return response.json();
  },

  // Suppliers
  getSuppliers: async (companyId) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/suppliers`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    return response.json();
  },

  createSupplier: async (companyId, data) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/suppliers`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });
    return response.json();
  },

  updateSupplier: async (companyId, id, data) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/suppliers/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });
    return response.json();
  },

  deleteSupplier: async (companyId, id) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/suppliers/${id}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    if (response.status === 204) return true;
    return response.json();
  },

  // Accounts
  getAccounts: async (companyId, params = {}) => {
    const token = localStorage.getItem('authToken');
    const query = new URLSearchParams(params).toString();
    const url = `${API_URL}/companies/${companyId}/accounts${query ? `?${query}` : ''}`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    return response.json();
  },
  createAccount: async (companyId, data) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/accounts`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });
    return response.json();
  },

  updateAccount: async (companyId, id, data) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/accounts/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });
    return response.json();
  },

  deleteAccount: async (companyId, id) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/accounts/${id}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    if (response.status === 204) return true;
    return response.json();
  },

  // Accountants
  getAccountants: async (companyId) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/accountants`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    return response.json();
  },

  createAccountant: async (companyId, data) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/accountants`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });
    return response.json();
  },

  updateAccountant: async (companyId, id, data) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/accountants/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });
    return response.json();
  },

  deleteAccountant: async (companyId, id) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/accountants/${id}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    if (response.status === 204) return true;
    return response.json();
  },

  getAddressByCep: async (cep) => {
    const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
    return response.json();
  },

  getCompanyByCnpj: async (cnpj) => {
    const response = await fetch(`https://brasilapi.com.br/api/cnpj/v1/${cnpj}`);
    if (!response.ok) {
      throw new Error('Erro ao buscar CNPJ');
    }
    return response.json();
  },

  getEmployees: async (companyId) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/employees`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    return response.json();
  },

  // Cost Centers
  getCostCenters: async (companyId) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/cost-centers`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    return response.json();
  },

  createCostCenter: async (companyId, data) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/cost-centers`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });
    return response.json();
  },

  importCostCenters: async (companyId, data) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/cost-centers/import`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });
    return response.json();
  },

  updateCostCenter: async (companyId, id, data) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/cost-centers/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });
    return response.json();
  },

  deleteCostCenter: async (companyId, id) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/cost-centers/${id}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    if (response.status === 204) return true;
    return response.json();
  },

  // Categories
  getCategories: async (companyId) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/categories`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    return response.json();
  },

  createCategory: async (companyId, data) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/categories`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });
    return response.json();
  },

  importCategories: async (companyId, data) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/categories/import`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });
    return response.json();
  },

  updateCategory: async (companyId, id, data) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/categories/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });
    return response.json();
  },

  deleteCategory: async (companyId, id) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/categories/${id}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    if (response.status === 204) return true;
    return response.json();
  },

  // Dashboard
  getGeneralDashboard: async (companyId, params = {}) => {
    const token = localStorage.getItem('authToken');
    const queryParams = new URLSearchParams(params);
    
    const response = await fetch(`${API_URL}/companies/${companyId}/general-dashboard?${queryParams.toString()}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    return response.json();
  },

  // Transactions
  getFinancialDashboard: async (companyId, params = {}) => {
    const token = localStorage.getItem('authToken');
    const queryParams = new URLSearchParams(params);
    
    let url = `${API_URL}/companies/${companyId}/transactions/dashboard`;
    if (companyId === 'all') {
      url = `${API_URL}/transactions/dashboard-all`;
    }

    const response = await fetch(`${url}?${queryParams.toString()}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    return response.json();
  },

  getEmployeeCharges: async (companyId, params = {}) => {
    const token = localStorage.getItem('authToken');
    const queryParams = new URLSearchParams(params);
    const response = await fetch(`${API_URL}/companies/${companyId}/financial/employee-charges?${queryParams.toString()}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    return response.json();
  },

  getTransactions: async (companyId, params = {}) => {
    const token = localStorage.getItem('authToken');
    
    // Default limit high to support client-side filtering until server-side is implemented
    const queryParams = new URLSearchParams({ limit: 10000, ...params });
    
    const response = await fetch(`${API_URL}/companies/${companyId}/transactions?${queryParams.toString()}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    return response.json();
  },

  getTransactionHistory: async (companyId, id) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/transactions/${id}/history`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    return response.json();
  },

  createTransaction: async (companyId, data) => {
    const token = localStorage.getItem('authToken');
    // Handle file upload if present in data (attachments)
    const isFormData = data instanceof FormData;
    
    const headers = {
      Authorization: `Bearer ${token}`,
    };
    
    if (!isFormData) {
      headers['Content-Type'] = 'application/json';
    }

    const response = await fetch(`${API_URL}/companies/${companyId}/transactions`, {
      method: 'POST',
      headers,
      body: isFormData ? data : JSON.stringify(data),
    });
    return response.json();
  },

  updateTransaction: async (companyId, id, data) => {
    const token = localStorage.getItem('authToken');
    const isFormData = data instanceof FormData;

    const headers = {
      Authorization: `Bearer ${token}`,
    };
    
    if (!isFormData) {
      headers['Content-Type'] = 'application/json';
    }

    const response = await fetch(`${API_URL}/companies/${companyId}/transactions/${id}`, {
      method: 'PUT',
      headers,
      body: isFormData ? data : JSON.stringify(data),
    });
    return response.json();
  },

  deleteTransaction: async (companyId, id) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/transactions/${id}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    if (response.status === 204) return true;
    return response.json();
  },

  deleteTransactionAttachment: async (companyId, transactionId, attachmentId) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/transactions/${transactionId}/attachments/${attachmentId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    if (response.status === 204) return true;
    return response.json();
  },

  // Absences
  getAbsences: async (companyId, params = {}) => {
    const token = localStorage.getItem('authToken');
    const filtered = Object.entries(params || {}).reduce((acc, [key, value]) => {
      if (value === undefined || value === null) return acc;
      const str = String(value);
      if (!str || str === 'null' || str === 'undefined') return acc;
      acc[key] = str;
      return acc;
    }, {});
    const queryParams = new URLSearchParams(filtered);
    const response = await fetch(`${API_URL}/companies/${companyId}/absences?${queryParams.toString()}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    return response.json();
  },

  createAbsence: async (companyId, data) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/absences`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });
    return response.json();
  },

  updateAbsence: async (companyId, id, data) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/absences/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });
    return response.json();
  },

  deleteAbsence: async (companyId, id) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/absences/${id}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    if (response.status === 204) return true;
    return response.json();
  },

  deleteAbsenceAttachment: async (companyId, absenceId, attachmentId) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/absences/${absenceId}/attachments/${attachmentId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    if (response.status === 204) return true;
    return response.json();
  },


  // Adjustments
  getAdjustments: async (companyId, params = {}) => {
    const token = localStorage.getItem('authToken');
    const queryParams = new URLSearchParams(params);
    const response = await fetch(`${API_URL}/companies/${companyId}/adjustments?${queryParams.toString()}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    return response.json();
  },

  createAdjustment: async (companyId, data) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/adjustments`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });
    return response.json();
  },

  updateAdjustment: async (companyId, id, data) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/adjustments/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });
    return response.json();
  },

  deleteAdjustment: async (companyId, id) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/adjustments/${id}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    return response.json();
  },

  getExecutedPoints: async (companyId, params) => {
    const token = localStorage.getItem('authToken');
    const queryString = new URLSearchParams(params).toString();
    const response = await fetch(`${API_URL}/companies/${companyId}/executed-points?${queryString}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    return response.json();
  },

  editDailyPoints: async (companyId, employeeId, data) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/employees/${employeeId}/executed-points/edit`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });
    return response.json();
  },

  getPointHistory: async (companyId, employeeId, date) => {
    const token = localStorage.getItem('authToken');
    let url = `${API_URL}/companies/${companyId}/employees/${employeeId}/executed-points/history`;
    if (date) {
        url += `?date=${date}`;
    }
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    return response.json();
  },

  getTimeKeepingOverview: async (companyId, params = {}) => {
    const token = localStorage.getItem('authToken');
    const queryParams = new URLSearchParams(params);
    const response = await fetch(`${API_URL}/companies/${companyId}/time-keeping/overview?${queryParams.toString()}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    return response.json();
  },

  getTimesheet: async (companyId, params = {}) => {
    const token = localStorage.getItem('authToken');
    const queryParams = new URLSearchParams(params);
    const response = await fetch(`${API_URL}/companies/${companyId}/time-keeping/timesheet?${queryParams.toString()}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    return response.json();
  },

  reconcilePreview: async (companyId, accountId, transactions) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/transactions/reconcile-preview`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ accountId, transactions }),
    });
    return response.json();
  },

  importOfxTransactions: async (companyId, accountId, transactions, updateBalance = false, finalBalance = null) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/transactions/import-ofx`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ accountId, transactions, updateBalance, finalBalance }),
    });
    return response.json();
  },

  // =============================================
  // SERVICE ORDERS MODULE
  // =============================================

  // Services CRUD
  getServices: async (companyId) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/services`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    });
    return response.json();
  },

  createService: async (companyId, data) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/services`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(data),
    });
    return response.json();
  },

  updateService: async (companyId, id, data) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/services/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(data),
    });
    return response.json();
  },

  deleteService: async (companyId, id) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/services/${id}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    });
    return response;
  },

  // Entities Lookup (polymorphic)
  getEntitiesByType: async (companyId, type) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/entities/${type}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    });
    return response.json();
  },

  getEntityById: async (companyId, type, id) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/entities/${type}/${id}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    });
    return response.json();
  },

  // Service Orders CRUD
  getServiceOrders: async (companyId, params = {}) => {
    const token = localStorage.getItem('authToken');
    const queryString = new URLSearchParams(params).toString();
    const url = queryString ? `${API_URL}/companies/${companyId}/service-orders?${queryString}` : `${API_URL}/companies/${companyId}/service-orders`;
    const response = await fetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    });
    return response.json();
  },

  getServiceOrderById: async (companyId, id) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/service-orders/${id}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    });
    return response.json();
  },

  createServiceOrder: async (companyId, data) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/service-orders`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(data),
    });
    return response.json();
  },

  updateServiceOrder: async (companyId, id, data) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/service-orders/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(data),
    });
    return response.json();
  },

  deleteServiceOrder: async (companyId, id) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/service-orders/${id}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    });
    return response;
  },

  // Service Order Executions
  getServiceOrderExecutions: async (companyId, params = {}) => {
    const token = localStorage.getItem('authToken');
    const queryString = new URLSearchParams(params).toString();
    const url = queryString ? `${API_URL}/companies/${companyId}/service-order-executions?${queryString}` : `${API_URL}/companies/${companyId}/service-order-executions`;
    const response = await fetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    });
    return response.json();
  },

  getServiceOrderExecutionsLight: async (companyId, params = {}) => {
    const token = localStorage.getItem('authToken');
    const queryString = new URLSearchParams(params).toString();
    const url = queryString ? `${API_URL}/companies/${companyId}/service-order-executions-light?${queryString}` : `${API_URL}/companies/${companyId}/service-order-executions-light`;
    const response = await fetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    });
    return response.json();
  },

  getServiceOrderExecutionById: async (companyId, id) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/service-order-executions/${id}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    });
    return response.json();
  },

  createServiceOrderExecution: async (companyId, data) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/service-order-executions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(data),
    });
    return response.json();
  },

  finishServiceOrderExecution: async (companyId, id, data) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/service-order-executions/${id}/finish`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(data),
    });
    return response.json();
  },

  cancelServiceOrderExecution: async (companyId, id, data) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/service-order-executions/${id}/cancel`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(data),
    });
    return response.json();
  },

  deleteServiceOrderExecution: async (companyId, id) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/service-order-executions/${id}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    });
    return response;
  },

  getServiceOrderDashboard: async (companyId, params = {}) => {
    const token = localStorage.getItem('authToken');
    const queryString = new URLSearchParams(params).toString();
    const url = queryString ? `${API_URL}/companies/${companyId}/service-orders-dashboard?${queryString}` : `${API_URL}/companies/${companyId}/service-orders-dashboard`;
    const response = await fetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    });
    return response.json();
  },

  getServiceOrderCalendar: async (companyId, params = {}) => {
    const token = localStorage.getItem('authToken');
    const queryString = new URLSearchParams(params).toString();
    const url = queryString ? `${API_URL}/companies/${companyId}/service-order-calendar?${queryString}` : `${API_URL}/companies/${companyId}/service-order-calendar`;
    const response = await fetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    });
    return response.json();
  },

  getServiceOrderCalendarSummary: async (companyId, params = {}) => {
    const token = localStorage.getItem('authToken');
    const queryString = new URLSearchParams(params).toString();
    const url = queryString ? `${API_URL}/companies/${companyId}/service-order-calendar-summary?${queryString}` : `${API_URL}/companies/${companyId}/service-order-calendar-summary`;
    const response = await fetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    });
    return response.json();
  },

  // Specific Documents
  getSpecificDocuments: async (params = {}) => {
    const token = localStorage.getItem('authToken');
    let url = `${API_URL}/specific-documents`;
    if (params) {
      const queryString = new URLSearchParams(params).toString();
      url += `?${queryString}`;
    }
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    return response.json();
  },

  createSpecificDocument: async (data) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/specific-documents`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });
    return response.json();
  },

  updateSpecificDocument: async (id, data) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/specific-documents/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });
    return response.json();
  },

  deleteSpecificDocument: async (id) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/specific-documents/${id}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    if (response.status === 204) return {};
    return response.json();
  },

  // Reasons (Motivos)
  getReasons: async (companyId) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/reasons`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    return response.json();
  },

  createReason: async (companyId, data) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/reasons`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });
    return response.json();
  },

  updateReason: async (companyId, id, data) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/reasons/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });
    return response.json();
  },

  deleteReason: async (companyId, id) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/companies/${companyId}/reasons/${id}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    if (response.status === 204) return {};
    return response.json();
  },
};

export default Api;
